import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Eye, 
  ShoppingCart, 
  Share2, 
  ArrowLeft,
  BarChart3,
  TrendingUp,
  Gamepad2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_tabletop-showcase/artifacts/4dj47y7o_file_000000006c38720abfd68ceed9ce979b~3.png";

// Session duration: 1 hour in milliseconds
const SESSION_DURATION = 60 * 60 * 1000;

// Check if session is valid
const isSessionValid = () => {
  const loginTime = localStorage.getItem("adminLoginTime");
  if (!loginTime) return false;
  const elapsed = Date.now() - parseInt(loginTime, 10);
  return elapsed < SESSION_DURATION;
};

// Get admin password from storage
const getAdminPassword = () => {
  if (isSessionValid()) {
    return localStorage.getItem("adminPassword");
  }
  localStorage.removeItem("adminPassword");
  localStorage.removeItem("adminLoginTime");
  return null;
};

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const adminPassword = getAdminPassword();

  useEffect(() => {
    if (!adminPassword) {
      toast.error("Please login first");
      navigate("/admin");
    }
  }, [adminPassword, navigate]);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/analytics/summary?password=${encodeURIComponent(adminPassword)}`
      );
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [adminPassword]);

  useEffect(() => {
    if (adminPassword) {
      fetchAnalytics();
    }
  }, [adminPassword, fetchAnalytics]);

  if (!adminPassword) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <img 
                src={LOGO_URL} 
                alt="Logo" 
                className="w-10 h-10 rounded-xl"
              />
              <div>
                <h1 className="font-serif font-bold text-lg text-slate-900">
                  Analytics Dashboard
                </h1>
                <p className="text-xs text-slate-500 font-sans">Track your game performance</p>
              </div>
            </div>

            <Link
              to="/admin/dashboard"
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-sans text-sm"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {loading ? (
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-stone-200">
                <div className="skeleton h-8 w-24 mb-2 rounded" />
                <div className="skeleton h-12 w-32 rounded" />
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid md:grid-cols-4 gap-6 mb-12">
              <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#7C3AED]/10 rounded-xl flex items-center justify-center">
                    <Gamepad2 className="w-5 h-5 text-[#7C3AED]" />
                  </div>
                  <span className="font-sans text-slate-600 text-sm">Total Games</span>
                </div>
                <p className="font-serif font-bold text-4xl text-slate-900">
                  {analytics?.total_games || 0}
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#7C3AED]/10 rounded-xl flex items-center justify-center">
                    <Eye className="w-5 h-5 text-[#7C3AED]" />
                  </div>
                  <span className="font-sans text-slate-600 text-sm">Total Views</span>
                </div>
                <p className="font-serif font-bold text-4xl text-slate-900">
                  {analytics?.total_views || 0}
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#7C3AED]/10 rounded-xl flex items-center justify-center">
                    <ShoppingCart className="w-5 h-5 text-[#7C3AED]" />
                  </div>
                  <span className="font-sans text-slate-600 text-sm">Purchase Clicks</span>
                </div>
                <p className="font-serif font-bold text-4xl text-slate-900">
                  {analytics?.total_purchase_clicks || 0}
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#F97316]/10 rounded-xl flex items-center justify-center">
                    <Share2 className="w-5 h-5 text-[#F97316]" />
                  </div>
                  <span className="font-sans text-slate-600 text-sm">Total Shares</span>
                </div>
                <p className="font-serif font-bold text-4xl text-slate-900">
                  {analytics?.total_shares || 0}
                </p>
              </div>
            </div>

            {/* Top Games */}
            <div className="bg-white rounded-3xl border border-stone-200 overflow-hidden mb-12">
              <div className="p-6 border-b border-stone-200 flex items-center gap-3">
                <div className="w-10 h-10 bg-[#7C3AED] rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-serif font-bold text-xl text-slate-900">Top Performing Games</h2>
                  <p className="font-sans text-sm text-slate-500">Ranked by total views</p>
                </div>
              </div>
              
              {analytics?.top_games && analytics.top_games.length > 0 ? (
                <div className="divide-y divide-stone-100">
                  {analytics.top_games.map((game, index) => (
                    <div 
                      key={index}
                      className="p-6 flex items-center justify-between hover:bg-stone-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-sans font-bold text-sm ${
                          index === 0 ? 'bg-[#EAB308] text-white' :
                          index === 1 ? 'bg-slate-400 text-white' :
                          index === 2 ? 'bg-[#CD7F32] text-white' :
                          'bg-stone-200 text-slate-600'
                        }`}>
                          {index + 1}
                        </span>
                        <span className="font-serif font-bold text-slate-900">{game.name}</span>
                      </div>
                      <div className="flex items-center gap-8">
                        <div className="text-center">
                          <p className="font-sans text-sm text-slate-500">Views</p>
                          <p className="font-serif font-bold text-slate-900">{game.views}</p>
                        </div>
                        <div className="text-center">
                          <p className="font-sans text-sm text-slate-500">Purchases</p>
                          <p className="font-serif font-bold text-[#7C3AED]">{game.purchases}</p>
                        </div>
                        <div className="text-center">
                          <p className="font-sans text-sm text-slate-500">Shares</p>
                          <p className="font-serif font-bold text-[#F97316]">{game.shares}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center">
                  <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="font-sans text-slate-600">No analytics data yet</p>
                  <p className="font-sans text-slate-400 text-sm">Views and clicks will appear here as users interact with games</p>
                </div>
              )}
            </div>

            {/* All Games Table */}
            {analytics?.all_games && analytics.all_games.length > 0 && (
              <div className="bg-white rounded-3xl border border-stone-200 overflow-hidden">
                <div className="p-6 border-b border-stone-200">
                  <h2 className="font-serif font-bold text-xl text-slate-900">All Games Performance</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-50">
                      <tr>
                        <th className="px-6 py-4 text-left font-sans font-medium text-slate-600 text-sm">Game</th>
                        <th className="px-6 py-4 text-center font-sans font-medium text-slate-600 text-sm">Views</th>
                        <th className="px-6 py-4 text-center font-sans font-medium text-slate-600 text-sm">Purchase Clicks</th>
                        <th className="px-6 py-4 text-center font-sans font-medium text-slate-600 text-sm">Shares</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {analytics.all_games.map((game, index) => (
                        <tr key={index} className="hover:bg-stone-50">
                          <td className="px-6 py-4 font-serif font-medium text-slate-900">{game.name}</td>
                          <td className="px-6 py-4 text-center font-sans text-slate-600">{game.views}</td>
                          <td className="px-6 py-4 text-center font-sans text-[#7C3AED] font-medium">{game.purchases}</td>
                          <td className="px-6 py-4 text-center font-sans text-[#F97316] font-medium">{game.shares}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
